import type { ReactNode } from 'react';
import type { DocumentGenerationKindDTO } from '@shared/schemas/document-generation';
import { cn } from '@/lib/utils';
import { DocumentGenerationKindIcon } from './ChatDocumentKindIcon';

/**
 * Coque de la carte (maquette « pixel perfect ») : surface carte, très grand rayon, ombre
 * très diffuse. Le conteneur de requête (`@container`) pilote le repli responsive : en
 * dessous de 42 rem, les actions passent sous le texte. Largeur : 48 rem plafonnée à la colonne
 * (`w-[48rem] max-w-full`) car un conteneur de requête n'a pas de largeur intrinsèque : un `w-full` seul
 * s'effondre dans un parent dimensionné par son contenu.
 */
export const CARD_SHELL_CLASS = '@container relative w-[48rem] max-w-full rounded-[1.5rem] border border-border/40 bg-card text-left shadow-[0_10px_40px_-10px_rgba(15,23,42,0.09)] dark:border-border dark:shadow-none';
export const CARD_SHELL_INTERACTIVE_CLASS = 'transition-shadow duration-200 hover:shadow-[0_14px_44px_-10px_rgba(15,23,42,0.14)]';

/** Zone cliquable étirée sur toute la carte (« stretched link ») sans imbriquer d'éléments interactifs. */
export const STRETCHED_HIT_AREA_CLASS = 'cursor-pointer outline-none after:absolute after:inset-0 after:rounded-[1.5rem] after:content-[\'\'] focus-visible:after:ring-2 focus-visible:after:ring-ring/40';

/** Boutons pilule gris clair de la carte (Télécharger, Versions, Réessayer). */
export const PILL_BUTTON_CLASS = 'h-[2.65rem] gap-1.5 rounded-full border-transparent bg-secondary px-[0.9375rem] text-sm font-medium text-foreground shadow-none hover:bg-secondary/70 [&_svg:not([class*=size-])]:size-[1.0625rem]';
export const ROUND_BUTTON_CLASS = 'size-[2.65rem] rounded-full border-transparent bg-secondary text-foreground shadow-none hover:bg-secondary/70';

const TILE_TINT: Record<DocumentGenerationKindDTO, string> = {
  docx: 'bg-blue-600/[0.08] dark:bg-blue-400/10',
  pdf: 'bg-rose-500/[0.08] dark:bg-rose-400/10',
  pptx: 'bg-orange-500/[0.09] dark:bg-orange-400/10',
  xlsx: 'bg-emerald-600/[0.09] dark:bg-emerald-400/10',
};

/** Tuile d'icône de format, teintée selon le format. `compact` : carte en cours (étapes). */
export function CardTile({ compact, kind }: { compact?: boolean; kind: DocumentGenerationKindDTO | null }) {
  return (
    <DocumentGenerationKindIcon
      className={kind ? TILE_TINT[kind] : undefined}
      kind={kind}
      size={compact ? 'card-sm' : 'card'}
    />
  );
}

const BADGE_TONES = {
  danger: 'border-destructive/20 bg-destructive/10 text-destructive',
  success: 'border-emerald-500/20 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300',
  warning: 'border-amber-500/25 bg-amber-500/10 text-amber-700 dark:text-amber-300',
} as const;

/** Pastille d'état (« Terminé », « Échec »…). */
export function StatusBadge({
  children,
  icon,
  tone,
}: {
  children: ReactNode;
  icon: ReactNode;
  tone: keyof typeof BADGE_TONES;
}) {
  return (
    <span
      className={cn(
        'inline-flex h-[1.5625rem] shrink-0 items-center gap-1.5 rounded-full border px-2.5 text-xs font-medium',
        BADGE_TONES[tone],
      )}
    >
      {icon}
      {children}
    </span>
  );
}

/** Fin filet vertical entre deux informations de la ligne méta. */
export function MetaSeparator() {
  return <span aria-hidden className="h-[0.6875rem] w-px shrink-0 bg-border" />;
}
